import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Optional,
  Output,
  Self,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { Subscription } from 'rxjs';

// Reusable "commit only on Enter / click" gate for Kendo dropdownlists,
// mirroring the proven logic first shipped inline on the report Report Type /
// Org Type / Tag type / Role dropdowns and on the language selector.
//
// Behaviour (WCAG 3.2.1 / 1.3.1 / 2.1.1):
//   - Arrow keys navigate the highlight only; the value is NOT committed.
//   - Enter / item-click commit — the (committed) output fires with the value.
//   - Esc / Tab close the popup and keep the value selected when the popup
//     opened (the navigated option is discarded).
//
// Usage: add `appKendoCommitGate` and bind your change handler to (committed)
// INSTEAD of (valueChange). For a dropdown inside a MatDialog, also set
// [inDialog]="true" so Esc closes only the popup, not the dialog.
//
// How it works: the value is snapshotted on (open). Kendo commits the
// navigated option on Esc (handleEscape) and Tab (commit-on-blur); the gate
// lets that commit fire so a valueChange refreshes the display, then rejects
// it (popup no longer "open") and reverts — via the bound NgControl for
// ngModel / formControlName, or via the Kendo value otherwise. A capture-phase
// keydown listener marks Esc/Tab as a cancel before Kendo runs; for dialogs a
// bubble-phase listener stops the Esc after Kendo has handled it so it never
// reaches the dialog's Esc-to-close handler.
@Directive({
  selector: 'kendo-dropdownlist[appKendoCommitGate]',
})
export class KendoCommitGateDirective implements AfterViewInit, OnDestroy {
  @Input() inDialog = false;
  @Output() committed = new EventEmitter<any>();

  private popupOpen = false;
  private commitGrace = false;
  private cancelClose = false;
  private escHandled = false;
  private committedValue: any = null;
  private subs: Subscription[] = [];
  private captureHandler?: (event: KeyboardEvent) => void;
  private documentHandler?: (event: KeyboardEvent) => void;
  private bubbleHandler?: (event: KeyboardEvent) => void;

  constructor(
    private host: ElementRef<HTMLElement>,
    @Optional() @Self() private dropdown?: DropDownListComponent,
    @Optional() @Self() private ngControl?: NgControl
  ) {}

  ngAfterViewInit(): void {
    if (!this.dropdown) {
      return;
    }
    const el = this.host.nativeElement;
    this.committedValue = this.dropdown.value;

    this.captureHandler = (event) => this.onCaptureKeyDown(event);
    el.addEventListener('keydown', this.captureHandler, true);
    this.documentHandler = (event) => {
      const target = event.target as Node | null;
      if (target && el.contains(target)) {
        this.onCaptureKeyDown(event);
      }
    };
    document.addEventListener('keydown', this.documentHandler, true);

    if (this.inDialog) {
      this.bubbleHandler = (event) => {
        if (event.key === 'Escape' && this.escHandled) {
          this.escHandled = false;
          event.stopPropagation();
        }
      };
      el.addEventListener('keydown', this.bubbleHandler, false);
    }

    this.subs.push(this.dropdown.open.subscribe(() => this.onOpen()));
    this.subs.push(this.dropdown.close.subscribe(() => this.onClose()));
    this.subs.push(
      this.dropdown.valueChange.subscribe((value) => this.onValueChange(value))
    );
  }

  ngOnDestroy(): void {
    const el = this.host.nativeElement;
    if (this.captureHandler) {
      el.removeEventListener('keydown', this.captureHandler, true);
    }
    if (this.documentHandler) {
      document.removeEventListener('keydown', this.documentHandler, true);
    }
    if (this.bubbleHandler) {
      el.removeEventListener('keydown', this.bubbleHandler, false);
    }
    this.subs.forEach((s) => s.unsubscribe());
  }

  private onOpen(): void {
    this.popupOpen = true;
    this.commitGrace = false;
    this.cancelClose = false;
    this.committedValue = this.dropdown ? this.dropdown.value : null;
  }

  private onClose(): void {
    this.popupOpen = false;
    if (this.cancelClose) {
      this.cancelClose = false;
      this.commitGrace = false;
      this.revert();
      return;
    }
    this.commitGrace = true;
    Promise.resolve().then(() => {
      this.commitGrace = false;
    });
  }

  private onValueChange(value: any): void {
    if (!this.popupOpen && !this.commitGrace) {
      this.revert();
      return;
    }
    this.committedValue = value;
    this.committed.emit(value);
  }

  private revert(): void {
    const control = this.ngControl ? this.ngControl.control : null;
    if (control) {
      if (control.value !== this.committedValue) {
        control.setValue(this.committedValue, { emitEvent: false });
      }
    } else if (this.dropdown && this.dropdown.value !== this.committedValue) {
      this.dropdown.value = this.committedValue;
    }
  }

  private onCaptureKeyDown(event: KeyboardEvent): void {
    if (!this.dropdown || !this.popupOpen) {
      return;
    }
    if (event.key === 'Escape') {
      // Let Kendo commit + close so a valueChange fires and the gate reverts.
      this.cancelClose = true;
      this.popupOpen = false;
      if (this.inDialog) {
        this.escHandled = true;
      }
      return;
    }
    if (event.key === 'Tab') {
      // No preventDefault — focus must still move to the next control.
      this.cancelClose = true;
      this.popupOpen = false;
      event.stopImmediatePropagation();
      this.dropdown.toggle(false);
    }
  }
}
